import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';

import { staffNotificationKeys } from '@/features/notifications/query-keys.js';
import { getAccessToken } from '@/shared/auth/auth-storage.js';
import { env } from '@/shared/config/env.js';

/** Mirrors `staff.notification` — BR-NOT-08 (enqueue staff → emit; no HTTP poll). */
const STAFF_NOTIFICATION_EVENT = 'staff.notification';
/** Same tenant staff room also receives chat list-update — backup if the dedicated signal is missed. */
const STAFF_CHAT_LIST_UPDATE_EVENT = 'conversation:list-update';

function refreshStaffNotificationQueries(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: staffNotificationKeys.all });
  // Keep open check-in/check-out screens fresh after titular decide (BR-SO-113/114/116 · BR-SO-100/101).
  // Root key mirrors `serviceOrderQueryKeys.all` without cross-feature import.
  void queryClient.invalidateQueries({ queryKey: ['service-order'] });
  // Staff Agenda/kanban after a consumer books or the board mutates (same staff room).
  void queryClient.invalidateQueries({ queryKey: ['appointment'] });
}

/**
 * Own socket factory (not `createConversationSocket` from `features/conversation` — cross-feature
 * imports are forbidden, see `apps-web-feature.mdc`). Same handshake/auth as chat: staff
 * auto-joins `tenant:{id}:staff` server-side on connect.
 */
function createStaffNotificationSocket(accessToken: string): Socket {
  const baseUrl = env.VITE_API_BASE_URL.length > 0 ? env.VITE_API_BASE_URL : window.location.origin;
  return io(baseUrl, {
    transports: ['websocket'],
    auth: { token: accessToken },
    reconnection: true,
    reconnectionDelay: 500,
  });
}

/** Opens a lightweight socket while authenticated; invalidates the inbox cache on signal. */
export function useStaffNotificationSocket(enabled: boolean): void {
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const token = getAccessToken();
    if (token === null) {
      return;
    }

    const socket = createStaffNotificationSocket(token);
    socketRef.current = socket;

    const onSignal = (): void => {
      refreshStaffNotificationQueries(queryClient);
    };

    socket.on('connect', onSignal);
    socket.on(STAFF_NOTIFICATION_EVENT, onSignal);
    socket.on(STAFF_CHAT_LIST_UPDATE_EVENT, (payload: { authorKind?: string }) => {
      if (payload.authorKind === 'consumer') {
        onSignal();
      }
    });

    const onVisible = (): void => {
      if (document.visibilityState === 'visible') {
        onSignal();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [enabled, queryClient]);
}
